import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight, Truck, Save } from "lucide-react";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { api, addGuestToken } from "../lib/api";
import { toast } from "sonner";

const ADDR_KEY = "ss_shipping_address";

const loadSavedAddress = () => {
  try { return JSON.parse(localStorage.getItem(ADDR_KEY)) || null; } catch { return null; }
};

export const Cart = () => {
  const { items, updateQty, removeItem, subtotal, unitPriceFor, wholesaleMode, setWholesaleMode, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();

  const [address, setAddress] = useState(() => loadSavedAddress() || {
    name: "", line1: "", line2: "", city: "", province: "England",
    postal_code: "", country: "GB", phone: "", email: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user && !address.name) setAddress((a) => ({ ...a, name: user.name || "", email: user.email || "" }));
  }, [user]);

  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((address.email || "").trim());
  const addrValid = address.name && address.line1 && address.city && address.postal_code && address.country && (user || emailValid);

  const checkout = async () => {
    if (items.length === 0) { toast.error("Your cart is empty"); return; }
    if (!addrValid) { toast.error(user ? "Please fill in your shipping address" : "Please fill in your address and a valid email for your receipt"); return; }
    localStorage.setItem(ADDR_KEY, JSON.stringify(address));
    setBusy(true);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, bulk: i.bulk })),
        origin_url: window.location.origin,
        is_wholesale: Boolean(wholesaleMode && user?.is_verified_teacher),
        shipping_address: address,
      });
      if (data.guest_token) addGuestToken(data.guest_token);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
    } finally { setBusy(false); }
  };

  const saveAsList = async () => {
    if (items.length === 0) { toast.error("Add items first"); return; }
    const name = window.prompt("Name your list (e.g. 'Year 3 · Autumn term')");
    if (!name) return;
    const recur = window.confirm("Recur every school term? We'll email you a one-tap reorder reminder when the new term starts.");
    try {
      await api.post("/shopping-lists", {
        name,
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, bulk: i.bulk })),
        recur_every_terms: recur,
      });
      toast.success(recur ? "List saved — we'll remind you every term" : "List saved");
      nav("/lists");
    } catch (e) {
      toast.error("Could not save list");
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <ShoppingBag className="w-12 h-12 mx-auto text-slate-300" />
        <h1 className="font-display text-3xl font-extrabold mt-4">Your cart is empty</h1>
        <p className="text-slate-600 mt-2">Time to fill it with notebooks, pens & backpacks.</p>
        <Link to="/shop"><Button size="lg" className="mt-6 rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="cart-empty-shop-btn">Start shopping</Button></Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold mb-8">Your cart</h1>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="space-y-3">
            {items.map((it) => {
              const price = unitPriceFor(it);
              return (
                <div key={it.product_id + it.bulk} className="flex gap-4 p-4 bg-white border border-slate-200 rounded-2xl" data-testid={`cart-item-${it.product_id}`}>
                  <img src={it.image} alt="" className="w-20 h-20 rounded-xl object-cover" />
                  <div className="flex-1">
                    <div className="font-display font-bold">{it.name}</div>
                    <div className="text-xs text-slate-500">{it.bulk ? "Bulk (wholesale)" : "Retail"}</div>
                    <div className="text-sm mt-1">${price.toFixed(2)} each</div>
                  </div>
                  <div className="flex flex-col items-end justify-between">
                    <button onClick={() => removeItem(it.product_id, it.bulk)} className="text-slate-400 hover:text-red-500" data-testid={`cart-remove-${it.product_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <div className="flex items-center gap-1 border border-slate-200 rounded-full">
                      <Button size="icon" variant="ghost" className="rounded-full h-7 w-7" onClick={() => updateQty(it.product_id, it.bulk, it.quantity - 1)} data-testid={`cart-decrease-${it.product_id}`}><Minus className="w-3 h-3" /></Button>
                      <div className="w-8 text-center text-sm font-semibold">{it.quantity}</div>
                      <Button size="icon" variant="ghost" className="rounded-full h-7 w-7" onClick={() => updateQty(it.product_id, it.bulk, it.quantity + 1)} data-testid={`cart-increase-${it.product_id}`}><Plus className="w-3 h-3" /></Button>
                    </div>
                    <div className="font-display font-bold">${(price * it.quantity).toFixed(2)}</div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="p-6 bg-white border border-slate-200 rounded-2xl" data-testid="shipping-address-form">
            <div className="flex items-center gap-2 mb-4">
              <Truck className="w-5 h-5 text-indigo-600" />
              <div>
                <div className="font-display font-bold text-lg">Shipping address</div>
                <div className="text-xs text-slate-500">Where should we send it? Used by our wholesaler for auto-fulfillment.</div>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2"><Label>Full name</Label><Input required value={address.name} onChange={(e) => setAddress({ ...address, name: e.target.value })} data-testid="address-name" /></div>
              <div className="sm:col-span-2"><Label>Address line 1</Label><Input required value={address.line1} onChange={(e) => setAddress({ ...address, line1: e.target.value })} placeholder="221B Baker Street" data-testid="address-line1" /></div>
              <div className="sm:col-span-2"><Label>Address line 2 (optional)</Label><Input value={address.line2} onChange={(e) => setAddress({ ...address, line2: e.target.value })} placeholder="Flat / building" data-testid="address-line2" /></div>
              <div><Label>City</Label><Input required value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} data-testid="address-city" /></div>
              <div><Label>Postcode</Label><Input required value={address.postal_code} onChange={(e) => setAddress({ ...address, postal_code: e.target.value })} placeholder="NW1 6XE" data-testid="address-postcode" /></div>
              <div><Label>Region / county</Label><Input value={address.province} onChange={(e) => setAddress({ ...address, province: e.target.value })} data-testid="address-province" /></div>
              <div>
                <Label>Country</Label>
                <select value={address.country} onChange={(e) => setAddress({ ...address, country: e.target.value })} className="w-full h-10 border border-slate-200 rounded-lg px-3 text-sm bg-white" data-testid="address-country">
                  <option value="GB">United Kingdom</option>
                  <option value="IE">Ireland</option>
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                  <option value="AU">Australia</option>
                  <option value="DE">Germany</option>
                  <option value="FR">France</option>
                </select>
              </div>
              <div><Label>Phone (recommended)</Label><Input value={address.phone} onChange={(e) => setAddress({ ...address, phone: e.target.value })} placeholder="+44 20 7946 0958" data-testid="address-phone" /></div>
              <div><Label>{user ? "Contact email" : "Email (for your receipt & tracking)"}</Label><Input type="email" required={!user} value={address.email} onChange={(e) => setAddress({ ...address, email: e.target.value })} data-testid="address-email" /></div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="p-6 bg-white border border-slate-200 rounded-2xl sticky top-24">
            {user?.is_verified_teacher && (
            <div className="flex items-center justify-between p-3 rounded-xl bg-indigo-50 border border-indigo-100 mb-4">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-indigo-700">Wholesale mode</div>
                <div className="text-xs text-slate-600">Verified teacher perk: wholesale pricing on entire cart</div>
              </div>
              <Switch checked={wholesaleMode} onCheckedChange={setWholesaleMode} data-testid="cart-wholesale-toggle" />
            </div>
            )}

            <div className="flex justify-between text-sm mb-2">
              <span className="text-slate-600">Subtotal</span>
              <span className="font-semibold" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm mb-4">
              <span className="text-slate-600">Shipping</span>
              <span className="text-emerald-600 font-semibold">Free (auto-fulfilled)</span>
            </div>
            <div className="flex justify-between text-lg font-display font-extrabold pt-4 border-t border-slate-200">
              <span>Total</span>
              <span data-testid="cart-total">${subtotal.toFixed(2)}</span>
            </div>
            {items.length > 0 && subtotal < 1 && (
              <div className="mt-2 text-xs text-amber-700" data-testid="cart-min-order-note">Minimum order is $1.00 — add one more item to check out.</div>
            )}
            <Button size="lg" disabled={busy || !addrValid} className="w-full mt-5 rounded-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60" onClick={checkout} data-testid="cart-checkout-btn">
              {busy ? "Redirecting…" : addrValid ? <>{user ? "Checkout with Stripe" : "Checkout as guest"} <ArrowRight className="w-4 h-4 ml-1" /></> : user ? "Add shipping address" : "Add address & email"}
            </Button>
            {!user && (
              <div className="mt-3 text-xs text-slate-500 text-center" data-testid="cart-guest-note">
                No account needed. Have one? <Link to="/login?redirect=/cart" className="text-indigo-600 font-semibold hover:underline" data-testid="cart-signin-link">Sign in</Link> for teacher discounts &amp; order history.
              </div>
            )}
            {user && (
              <Button size="sm" variant="outline" onClick={saveAsList} className="w-full mt-2 rounded-full" data-testid="cart-save-as-list">
                <Save className="w-4 h-4 mr-1" />Save as class list
              </Button>
            )}
            <button onClick={clear} className="w-full text-xs text-slate-500 hover:text-red-500 mt-3" data-testid="cart-clear-btn">Clear cart</button>
          </div>
        </div>
      </div>
    </div>
  );
};
